import React, { useRef, useEffect, useCallback } from "react";

export default function WaveformDisplay({ audioBuffer, trimStart = 0, trimEnd = 1, onTrimChange, height = 80 }) {
  const canvasRef = useRef(null);
  const isDraggingRef = useRef(null); // "start" | "end" | null

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !audioBuffer) return;

    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const h = canvas.height;
    const data = audioBuffer.getChannelData(0);
    const step = Math.ceil(data.length / width);

    ctx.clearRect(0, 0, width, h);

    // Draw dimmed regions outside trim
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fillRect(0, 0, trimStart * width, h);
    ctx.fillRect(trimEnd * width, 0, width - trimEnd * width, h);

    // Draw waveform
    ctx.beginPath();
    ctx.strokeStyle = "#EE772F";
    ctx.lineWidth = 1;
    const mid = h / 2;

    for (let i = 0; i < width; i++) {
      let min = 1, max = -1;
      const sampleStart = Math.floor(i * step);
      for (let j = 0; j < step && sampleStart + j < data.length; j++) {
        const val = data[sampleStart + j];
        if (val < min) min = val;
        if (val > max) max = val;
      }
      ctx.moveTo(i, mid + min * mid);
      ctx.lineTo(i, mid + max * mid);
    }
    ctx.stroke();

    // Draw trim handles
    const drawHandle = (x) => {
      ctx.fillStyle = "#fff";
      ctx.fillRect(x - 2, 0, 4, h);
      ctx.fillStyle = "#EE772F";
      ctx.fillRect(x - 1, 0, 2, h);
    };
    drawHandle(trimStart * width);
    drawHandle(trimEnd * width);
  }, [audioBuffer, trimStart, trimEnd]);

  useEffect(() => {
    draw();
  }, [draw]);

  // Resize canvas to container
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = height;
      draw();
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [draw, height]);

  const getPositionFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  };

  const handlePointerDown = (e) => {
    if (!onTrimChange) return;
    const pos = getPositionFromEvent(e);
    const startDist = Math.abs(pos - trimStart);
    const endDist = Math.abs(pos - trimEnd);
    isDraggingRef.current = startDist < endDist ? "start" : "end";
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current || !onTrimChange) return;
    const pos = getPositionFromEvent(e);
    if (isDraggingRef.current === "start") {
      onTrimChange(Math.min(pos, trimEnd - 0.01), trimEnd);
    } else {
      onTrimChange(trimStart, Math.max(pos, trimStart + 0.01));
    }
  };

  const handlePointerUp = () => {
    isDraggingRef.current = null;
  };

  return (
    <canvas
      ref={canvasRef}
      className="daw-waveform-canvas"
      style={{ height }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    />
  );
}
